/**
 * View-model shapes for the redesigned CRL list-view page (PP-3752).
 *
 * These are PURELY presentational projections derived in
 * `CxpCrlDetailSelectors.getSelectedRequestDisplay` from the domain model
 * (`CrlRequestDetail` + the raw task-list request + `CxpTasksState` support
 * data). Components consume these — they never compute the rules themselves
 * (see cxp-state "business rules in selectors").
 */

import { CrlAssigneeKind, CrlItemStatus, CrlRequestItem } from './cxp-crl-detail.model';

/** Overall completion buckets driving the header progress tile (REQ-8). */
export enum CrlCompletionState {
  NOT_STARTED = 'NOT_STARTED',
  PARTIALLY_COMPLETE = 'PARTIALLY_COMPLETE',
  COMPLETE = 'COMPLETE',
}

/** Overall completion summary for the progress tile (REQ-8). */
export interface CrlProgressSummary {
  /** Items counted as done (FINISHED / NOT_APPLICABLE). */
  done: number;
  /** Total items across all sections. */
  total: number;
  /** 0–100, rounded. 0 when there are no items. */
  percent: number;
  state: CrlCompletionState;
}

/** Counts for the three granular metric tiles (REQ-8). */
export interface CrlTileCounts {
  /** WAITING + OVERDUE items ("Waiting for You"). */
  waitingForYou: number;
  /** IN_REVIEW items ("In Review"). */
  inReview: number;
  /** NEEDS_UPDATE items ("Needs Update"). */
  needsUpdate: number;
  /** OVERDUE items — surfaced as the red callout inside Waiting for You. */
  overdue: number;
}

/** Drives the action-required banner (REQ-14). */
export interface CrlActionRequiredSummary {
  /** Overdue + needs-update item count. */
  count: number;
  hasOverdue: boolean;
  hasNeedsUpdate: boolean;
  /** True when the banner should render at all. */
  show: boolean;
}

/**
 * A section projected for display: items split into open vs finished, with the
 * "is the whole section finished" flag the container uses to drop it below the
 * completed-sections divider (REQ-4).
 */
export interface CrlDisplaySection {
  id: number;
  name: string;
  ordinal: number;
  /** 1-based section identifier letter, e.g. `A`, `B` … (REQ-4). */
  identifier: string;
  /** Items still needing attention, in ordinal order. */
  openItems: CrlRequestItem[];
  /** Finished/N-A items, collapsed at the section bottom. */
  finishedItems: CrlRequestItem[];
  /** True when every item is finished/N-A (section is "done"). */
  isComplete: boolean;
}

/** A firm team member shown in the right rail (REQ-3). */
export interface CrlTeamMember {
  staffId: number;
  name: string;
  email: string;
  /** Firm owner (reviewer) — starred and listed first. */
  isOwner: boolean;
}

/** A client-access person (contact or secure-link guest) shown in the right rail (REQ-3). */
export interface CrlClientContact {
  kind: CrlAssigneeKind;
  /** Stable template track key — contacts key on contactId, guests on secureLinkId. */
  key: string;
  /** Null for secure-link guests. */
  contactId: number | null;
  name: string | null;
  email: string | null;
  /** Client owner — starred and listed first. */
  isOwner: boolean;
  /** The secure_link id — the revoke handle; null for contacts. */
  secureLinkId: number | null;
}

/** The set of statuses counted as "done" for progress + section completion. */
export const CRL_DONE_STATUSES: ReadonlySet<CrlItemStatus> = new Set([
  CrlItemStatus.FINISHED,
  CrlItemStatus.NOT_APPLICABLE,
]);

/**
 * Whether the bulk-actions entry point is offered on the list.
 *
 * Bulk actions (REQ-11) are a shell: the toolbar and selection mode work, but no
 * action is wired to a mutation. The entry point stays hidden until they are, so
 * the surface is never reachable in a released build. Flipping this to `true` is
 * the whole of re-enabling it — the implementation below is kept, not deleted.
 */
export const CRL_BULK_ACTIONS_ENABLED = false;

/**
 * Batch operations offered by the bulk-actions toolbar (REQ-11, SHELL ONLY).
 * The buttons render and the selection mode works, but none are wired to a real
 * mutation yet — see `CxpNewCrlPageComponent.onBulkAction` (TODO).
 */
export enum CrlBulkAction {
  MARK_FINISHED = 'MARK_FINISHED',
  MARK_NOT_APPLICABLE = 'MARK_NOT_APPLICABLE',
  REASSIGN = 'REASSIGN',
}

/**
 * Whether a row may be selected in bulk-actions mode. Restricted items are
 * non-selectable per spec (a firm-restricted item can't be batch-acted on by
 * the client).
 */
export function isCrlItemBulkSelectable(item: CrlRequestItem): boolean {
  return !item.isRestricted;
}
